import { put, call, takeLatest, type CallEffect, type PutEffect, type ForkEffect } from 'redux-saga/effects';
import { ProjectActionType } from './enums';
import { buildQuotationPromoRequest } from '../../../utilities/quotationPromo';
import { requestProjectLedger } from '../ledgerRequest';
import { handleEmptyNullValues, setItems, toast, transformUploadData, uploadDocumentArr } from '@utilities';
import { isProductChosen } from '@utilities/productSelection';
import {
	activateDocument,
	completeRejectProjectActivity,
	createQuotation,
	getDefaultQuotations,
	getPreDesignQuotation,
	getPreDesignQuotationSavedState,
	getPrediction,
	getProjectDetails,
	getProjectActivities,
	sendOTP,
	uploadActivityDocuments,
	uploadFloorPlan,
	verifyOTP,
	fetchActivityDocuments,
	getProjectCurrentActivity,
	floorPlanValidator,
	uploadProjectDetails,
	getAllProjects,
	fetchProductCategories,
	fetch2DJson,
	fetch3DJson,
	fetchLeadProjects,
	fetchAreaListDropdown,
	fetchPaymentSlips,
	getLedgerData,
	getAllPayments,
	fetchCustomerSlip,
	uploadMoreActivityDocs,
	getCutlistData,
	getOptimiserData,
	getOptimizedCutlistData,
	uploadWarrantyClaimDocuments,
	checkWarrantyValidity,
	createWarrantyClaim,
	getWarrantyClaim,
	getAllWarrantyClaims,
	getWarrantyClaimsByLeadId,
	getWarrantyCodeDetails,
	getAssignedProjects,
	getBuilderStates,
	getPromoCodeHistory,
	calculatePromoCodeAmt,
	markCutlistViewed,
	markBomViewed
} from './api';

import { AnyAction } from 'redux';
import { type ProjectAction } from './interfaces';
import { getPredictionId } from '../customer/api';

type ProjectSagaEffect = Generator<CallEffect<any> | PutEffect<ProjectAction>>;
type ProjectSagaFormEffect = Generator<ForkEffect<void>>;

export function* addProjectDetailsEffect(action: AnyAction): ProjectSagaEffect {
	const { formData, successCB, errorCB } = action.payload;

	try {
		// const { data } = yield call(addProjectDetails, formData);
		successCB();
	} catch (error: any) {
		errorCB(error?.message);
	}
}

export function* getPreDesignQuotationEffect(action: AnyAction): any {
	const { successCB, selectedAreas, budget, errorCB, calculationId, projectId } = action.payload;
	const transformedData = selectedAreas.map(area => ({
		...area,
		productCategories: Object.entries(area.productCategories)
			.filter(([_, value]) => value)
			.map(([key]) => key)
	}));

	try {
		const { data }: any = yield call(getPreDesignQuotation, {
			areas: transformedData,
			budget: budget,
			calculationId,
			projectId
		});

		yield put({
			type: ProjectActionType.STORE_PRE_DESIGN_QUOTATION,
			payload: data
		});
		successCB(data);
	} catch (error: any) {
		yield put({
			type: ProjectActionType.ERROR_PRE_DESIGN_QUOTATION,
			payload: {
				error
			}
		});
		if (errorCB) {
			errorCB(error?.message);
		}
	}
}

export function* getDefaultQuotationsEffect(action: AnyAction): any {
	const { selectedAreas, budget, errorCB, successCB, calculationId, projectId } = action.payload;
	const transformedData = selectedAreas.map(area => ({
		...area,
		productCategories: normalizeAreaProductCategoriesForRequest(area.productCategories)
	}));
	try {
		const { data }: any = yield call(getDefaultQuotations, {
			areas: transformedData,
			budget: budget,
			calculationId,
			projectId
		});

		yield put({
			type: ProjectActionType.STORE_DEFAULT_QUOTATION,
			payload: data
		});
		if (successCB) {
			successCB(data);
		}
	} catch (error: any) {
		yield put({
			type: ProjectActionType.ERROR_DEFAULT_QUOTATION,
			payload: {
				error
			}
		});
		if (errorCB) {
			errorCB(error?.message);
		}
	}
}

export function* fetchPreDesignSavedStateEffect(action: AnyAction): any {
	const { projectId, successCB, errorCB } = action.payload || {};
	try {
		const { data }: any = yield call(getPreDesignQuotationSavedState, projectId);
		yield put({
			type: ProjectActionType.STORE_DEFAULT_QUOTATION,
			payload: data || {}
		});
		if (successCB) {
			successCB(data || {});
		}
	} catch (error: any) {
		yield put({
			type: ProjectActionType.ERROR_DEFAULT_QUOTATION,
			payload: {
				error
			}
		});
		if (errorCB) {
			errorCB(error?.message);
		}
	}
}

export function* updateFloorPlanEffect(action: AnyAction): ProjectSagaEffect {
	const { formData, successCB, errorCB, finalCB } = action.payload;
	const updateFloorPlanDocRequest = {
		...formData,
		attachments: formData?.files?.files.map((file: any) => ({
			file: file.name
		}))
	};

	try {
		const { data }: any = yield call(uploadFloorPlan, handleEmptyNullValues(updateFloorPlanDocRequest));
		const { attachments, category, subCategory, documentId } = data;
		const { arrPromises, uploadedDocuments } = uploadDocumentArr(
			{
				documents: [
					{
						category: formData.category,
						referenceType: formData.referenceType,
						subCategory: formData.subCategory,
						files: formData.files.files
					}
				]
			},
			[{ category, subCategory, attachments, documentId }]
		);
		yield call(async () => await Promise.all(arrPromises));
		if (uploadedDocuments.length > 0) {
			yield call(activateDocument, { documentIds: uploadedDocuments });
		}

		let chatGptData;
		while (true) {
			try {
				chatGptData = yield call(floorPlanValidator, { docId: uploadedDocuments[0] });
				if (chatGptData?.data && Object.keys(chatGptData.data).length > 0) {
					break;
				}
				chatGptData = yield call(floorPlanValidator, { docId: uploadedDocuments[0] });
			} catch (validatorError) {
				throw validatorError;
			}
		}
		const message = chatGptData?.data?.choices[0]?.message?.content;
		if (message.toLowerCase().includes('yes')) {
			successCB(data);
		} else {
			throw new Error('Upload a valid floor plan');
		}
	} catch (error: any) {
		errorCB(error?.message);
	} finally {
		finalCB();
	}
}

export function* uploadProjectDetailsEffect(action: AnyAction): ProjectSagaEffect {
	const { formData, successCB, errorCB, finalCB } = action.payload;

	const transformedData = {
		leadId: formData.leadId,
		budget: formData.budget,
		documentId: formData.docId,
		project: formData.projectDetails
			? {
					projectName: formData.projectDetails.savedProjectName,
					projectId: formData.projectDetails.savedProjectId
				}
			: formData.project
	};
	try {
		const { data }: any = yield call(uploadProjectDetails, handleEmptyNullValues(transformedData));
		const { projectId, projectName } = data;

		yield put({
			type: ProjectActionType.SET_PROJECT_DETAILS,
			payload: { projectId, projectName, budget: formData?.budget }
		});

		successCB(transformedData?.documentId, projectId);
	} catch (error: any) {
		errorCB(error?.message);
	} finally {
		finalCB();
	}
}

export function* sendCustomerOTPEffect(action: AnyAction): any {
	const {
		leadDetails: { phoneNumber, name },
		successCB,
		errorCB
	} = action.payload;
	try {
		const data: any = yield call(sendOTP, { phone_number: phoneNumber, name });
		const payload = data.data;

		yield put({ type: ProjectActionType.SEND_CUSTOMER_OTP_SUCCESS, payload });
		successCB();
	} catch (err: any) {
		yield put({ type: ProjectActionType.SEND_CUSTOMER_OTP_FAILED });
		errorCB(err?.response?.data?.message);
	}
}

export function* verifyCustomerOTPEffect(action: AnyAction): any {
	const {
		sessionID,
		verifyOTPDetails: { otp },
		successCB,
		errorCB
	} = action.payload;
	const urlParams = new URLSearchParams(window.location.search);
	const source = urlParams.get('source')?.toUpperCase();
	try {
		const data: any = yield call(verifyOTP, {
			session_id: sessionID,
			otp,
			...(source && { source })
		});
		const payload = data.data;
		yield call(setItems, payload.auth_response);
		if (successCB) successCB();
	} catch (err: any) {
		yield put({ type: ProjectActionType.VERIFY_CUSTOMER_OTP_FAILED });
		errorCB(err.response?.data?.message);
	}
}

export function* resendCustomerOTPEffect(action: AnyAction): any {
	try {
		const { phoneNumber } = action.payload;
		const data: any = yield call(sendOTP, {
			phone_number: `${phoneNumber}`
		});
		const payload = data.data;
		yield put({ type: ProjectActionType.SEND_CUSTOMER_OTP_SUCCESS, payload });
	} catch (err: any) {
		yield put({ type: ProjectActionType.SEND_CUSTOMER_OTP_FAILED });
		toast('ERROR', err.response?.data?.message);
	}
}

export function* getPredictionIdEffect(action: AnyAction): any {
	const { successCB, documentId, projectId, errorCB } = action.payload;
	try {
		const { data }: any = yield call(getPredictionId, { documentId, projectId });
		yield put({
			type: ProjectActionType.STORE_PREDICTION_ID,
			payload: data
		});
		successCB(data);
	} catch (error) {
		if (errorCB) errorCB;
	}
}

export function* getPredictionEffect(action: AnyAction): any {
	const { successCB, errorCB, id } = action.payload;
	try {
		const { data }: any = yield call(getPrediction, id);

		if (data.status === 'succeeded') {
			yield put({
				type: ProjectActionType.STORE_AREAS,
				payload: data
			});
			successCB && successCB(data);
		} else if (data.status === 'rejected') {
			yield put({
				type: ProjectActionType.PREDICTION_FAILED,
				payload: { error: 'Prediction was rejected.' }
			});
			errorCB && errorCB('Prediction was rejected.');
		} else {
			successCB && successCB(data);
		}
	} catch (error: any) {
		yield put({
			type: ProjectActionType.PREDICTION_FAILED,
			payload: { error }
		});
		errorCB(error.message);
	}
}

// Helper function to revert area names back to original form
const revertAreaName = areaName => {
	// Remove _floor_X suffix if it exists
	const floorSuffixRegex = /_floor_\d+$/;
	return areaName.replace(floorSuffixRegex, '');
};

// Helper function to extract floor number from area key
const extractFloorNumber = areaKey => {
	const floorMatch = areaKey.match(/_floor_(\d+)$/);
	return floorMatch ? parseInt(floorMatch[1], 10) : 0; // Default to 0 (ground floor) if no floor suffix
};

/** Normalize productCategories to string[] — same contract as pre-design / default quotation APIs */
const normalizeAreaProductCategoriesForRequest = (pc: any): string[] => {
	if (pc == null) return [];
	if (Array.isArray(pc)) return pc;
	if (typeof pc === 'object') {
		return Object.entries(pc)
			.filter(([, v]) => v)
			.map(([k]) => k);
	}
	return [];
};

/**
 * Floor-plan selected areas for create / promo quotation calls — aligned with GET_PRE_DESIGN_QUOTATION payload.
 */
export const transformSelectedAreasForQuotationRequest = (selectedAreas: any): any[] | undefined => {
	if (selectedAreas == null) return undefined;
	const list = Array.isArray(selectedAreas) ? selectedAreas : selectedAreas?.areas;
	if (!Array.isArray(list) || list.length === 0) return undefined;
	return list.map((area: any) => ({
		...area,
		productCategories: normalizeAreaProductCategoriesForRequest(area.productCategories)
	}));
};

/** Matches explorer / CustomizationSidebar costing — only `isSelected` products. */
const isChosenForQuotationPayload = (product: any): boolean => isProductChosen(product);

const resolveProductPackageType = (product: any, fallback: string): string => {
	const raw = product?.type;
	if (raw != null && String(raw).trim()) {
		const t = String(raw).trim().toUpperCase();
		if (['LITE', 'ELEGANT', 'ELITE', 'CUSTOM'].includes(t)) return t;
	}
	const sp = String(product?.sourcePackage || '').toLowerCase();
	if (sp.includes('lite')) return 'LITE';
	if (sp.includes('elegant')) return 'ELEGANT';
	if (sp.includes('elite')) return 'ELITE';
	return fallback;
};

export const transformQuotationRequest = quotationData => {
	const transformedData: { total: number; areas: any[] } = {
		total: quotationData.grandTotal,
		areas: []
	};

	const extractProducts = (groupsObj, intoArray, packageType) => {
		Object.keys(groupsObj || {}).forEach(groupKey => {
			const group = groupsObj[groupKey];

			if (Array.isArray(group)) {
				group.forEach(product => {
					if (isChosenForQuotationPayload(product)) {
						const resolvedType = resolveProductPackageType(product, packageType);
						intoArray.push({
							productName: product.productName,
							unitOfMeasure: product.unitOfMeasure,
							measurement: {
								width: product.measurement?.width || 0,
								depth: product.measurement?.depth || 0,
								height: product.measurement?.height || 0,
								area: product.measurement?.area || 0
							},
							productId: product?.productId,
							price: product.price,
							unitCost: product.unitCost,
							quantity: product.quantity,
							specification: product.specification || '',
							description: product.description || '',
							calculation: product.calculation,
							type: resolvedType,
							productSubCategory: product.productSubCategory,
							productCategory: product.productCategory,
							imageUrl: product.imageUrl,
							active: true,
							warrantyCodes: product.warrantyCodes,
							builder: product.builder,
							paintSurfaces: product.paintSurfaces || [],
							shutterWidths: product.shutterWidths || [],
							materialCode: product.productName.toLowerCase().includes('wall tile')
								? product.materialCode || '26033D4'
								: product.materialCode || ''
						});
					}
				});
			} else if (group && typeof group === 'object') {
				Object.keys(group).forEach(subKey => {
					const subGroup = group[subKey];
					if (Array.isArray(subGroup)) {
						subGroup.forEach(product => {
							if (isChosenForQuotationPayload(product)) {
								const resolvedType = resolveProductPackageType(product, packageType);
								intoArray.push({
									productName: product.productName,
									unitOfMeasure: product.unitOfMeasure,
									measurement: {
										width: product.measurement?.width || 0,
										depth: product.measurement?.depth || 0,
										height: product.measurement?.height || 0,
										area: product.measurement?.area || 0
									},
									productId: product.productId,
									price: product.price,
									unitCost: product.unitCost,
									quantity: product.quantity,
									specification: product.specification || '',
									description: product.description || '',
									calculation: product.calculation,
									type: resolvedType,
									productSubCategory: product.productSubCategory,
									productCategory: product.productCategory,
									imageUrl: product.imageUrl,
									active: true,
									warrantyCodes: product.warrantyCodes,
									builder: product.builder,
									shutterWidths: product.shutterWidths || [],
									paintSurfaces: product.paintSurfaces || [],
									materialCode: product.productName.toLowerCase().includes('wall tile')
										? product.materialCode || '26033D4'
										: product.materialCode || ''
								});
							}
						});
					}
				});
			}
		});
	};

	// Base quotation package keys used across the app
	const BASE_TYPE_KEYS = ['liteQuotation', 'elegantQuotation', 'eliteQuotation'];

	Object.keys(quotationData).forEach(areaKey => {
		const area = quotationData[areaKey];
		if (!area || typeof area !== 'object' || !area.areaEnum) return;

		const isCustomMode = !!area.customQuotation?.isSelected;
		const products: any[] = [];

		if (isCustomMode) {
			// Custom tab: the merged snapshot is the source of truth — base tiers often have
			// isSelected false and stale flags, so only scanning lite/elegant/elite drops the area.
			const customProdObj = area.customQuotation?.products;
			const useCustomSnapshot =
				!!area.customQuotation?.isSelected &&
				customProdObj &&
				typeof customProdObj === 'object' &&
				Object.keys(customProdObj).length > 0;

			if (useCustomSnapshot) {
				const fallbackPkg = String(area.customQuotation?.originalActiveType || 'elegantQuotation')
					.replace(/Quotation$/i, '')
					.toUpperCase();
				extractProducts(customProdObj, products, fallbackPkg || 'ELEGANT');
			} else {
				// Custom metadata without a snapshot — aggregate from base packages
				BASE_TYPE_KEYS.forEach(baseKey => {
					const typeObj = area[baseKey];
					if (!typeObj) return;
					const pkgType = baseKey.replace('Quotation', '').toUpperCase();

					extractProducts(typeObj.products, products, pkgType);

					Object.keys(typeObj)
						.filter(k => k.startsWith('xorGroup'))
						.forEach(xorKey => {
							extractProducts(typeObj[xorKey], products, pkgType);
						});
				});
			}

			// Revert area name to original form (remove _floor_X suffix) but keep each area separate
			const originalAreaName = revertAreaName(area.areaName);
			const floorNumber = extractFloorNumber(areaKey);
			const customSubTotal = area.customQuotation?.areaTotal ?? area.areaTotal ?? 0;

			if (products.length > 0) {
				transformedData.areas.push({
					packageName: 'CUSTOM',
					products,
					subTotal: customSubTotal,
					areaEnum: area.areaEnum,
					areaName: originalAreaName,
					floor: floorNumber,
					surfaces: area.surfaces || []
				});
			}
		} else {
			// Normal mode — single selected package per area
			let selectedKey = Object.keys(area).find(k => k.endsWith('Quotation') && area[k]?.isSelected);
			if (!selectedKey) {
				const tiers = ['elegantQuotation', 'liteQuotation', 'eliteQuotation'] as const;
				selectedKey = tiers.find(k => area[k]?.isSelected) || tiers.find(k => area[k]?.products) || undefined;
			}
			if (!selectedKey) return;

			const sel = area[selectedKey];
			const pkgType = selectedKey.replace('Quotation', '').toUpperCase();

			// regular product groups
			extractProducts(sel.products, products, pkgType);

			// any xorGroup_* on the quotation itself
			Object.keys(sel)
				.filter(k => k.startsWith('xorGroup'))
				.forEach(xorKey => {
					extractProducts(sel[xorKey], products, pkgType);
				});

			// Cross-package variant selections: when the explorer sidebar (showAllPackages)
			// picks a variant from a different tier, the selected product lives in that
			// other tier's products while the active tier's copy is deselected.
			// Collect productIds already extracted to avoid duplicates.
			const extractedIds = new Set(products.map(p => p.productId));
			const crossPackageProducts: any[] = [];
			BASE_TYPE_KEYS.forEach(otherKey => {
				if (otherKey === selectedKey) return;
				const otherType = area[otherKey];
				if (!otherType?.products) return;
				const otherPkgType = otherKey.replace('Quotation', '').toUpperCase();
				extractProducts(otherType.products, crossPackageProducts, otherPkgType);
			});
			// Only add cross-package products not already present from the selected tier
			crossPackageProducts.forEach(p => {
				if (!extractedIds.has(p.productId)) {
					extractedIds.add(p.productId);
					products.push(p);
				}
			});

			// Revert area name to original form (remove _floor_X suffix) but keep each area as separate entry
			const originalAreaName = revertAreaName(area.areaName);
			// Extract floor number from the area key
			const floorNumber = extractFloorNumber(areaKey);

			if (products.length > 0) {
				transformedData.areas.push({
					packageName: pkgType,
					products,
					subTotal: sel.areaTotal,
					areaEnum: area.areaEnum,
					areaName: originalAreaName,
					floor: floorNumber,
					surfaces: area.surfaces || []
				});
			}
		}
	});
	return transformedData;
};

export function* createQuotationEffect(action: AnyAction): any {
	const {
		currentQuotation,
		successCB,
		errorCB,
		leadId,
		projectId,
		predictionId,
		promoCode,
		calculationId,
		selectedAreas
	} = action.payload;

	const transformedRequest = transformQuotationRequest(currentQuotation);
	const selectedAreasPayload = transformSelectedAreasForQuotationRequest(selectedAreas);

	if (import.meta.env.DEV) {
		console.debug(
			'[CreateQuotation] Payload:',
			transformedRequest.areas?.map((a: any) => ({
				area: a.areaName,
				productCount: a.products?.length,
				products: a.products?.map((p: any) => `${p.productName} (₹${p.price})`)
			}))
		);
	}

	try {
		const { data }: any = yield call(createQuotation, {
			...transformedRequest,
			projectId,
			leadId,
			predictionId,
			promoCode,
			calculationId,
			erp: true,
			...(selectedAreasPayload ? { selectedAreas: selectedAreasPayload } : {})
		});

		const transformedData = {
			...data,
			areas: data?.areas?.map((area: any) => ({
				...area,
				areaName: area.name,
				floor: area.floor // Preserve floor information from backend response
			}))
		};
		successCB(transformedData);
	} catch (error: any) {
		errorCB(error?.message);
	} finally {
		yield put({ type: ProjectActionType.CREATE_QUOTATION_LOADING, payload: false });
	}
}

export function* fetchProjectDetailsEffect(action: AnyAction): any {
	const { projectId, errorCB, successCB } = action.payload;
	try {
		const { data }: any = yield call(getProjectDetails, projectId);
		yield put({ type: ProjectActionType.STORE_PROJECT_DETAILS, payload: { data } });
		if (successCB) successCB(data);
	} catch (error: any) {
		if (errorCB) errorCB(error?.message);
		yield put({
			type: ProjectActionType.ERROR_FETCH_PROJECTS_DETAILS,
			payload: {
				error
			}
		});
	}
}

export function* fetchProjectActivitiesEffect(action: AnyAction): any {
	const { projectID, errorCB } = action.payload;

	try {
		const { data }: any = yield call(getProjectActivities, projectID);

		yield put({
			type: ProjectActionType.STORE_PROJECT_ACTIVITIES,
			payload: data
		});
	} catch (error: any) {
		yield put({
			type: ProjectActionType.ERROR_PROJECT_ACTIVITIES,
			payload: {
				error
			}
		});
		errorCB(error?.message);
	}
}

export function* completeRejectProjectActivityEffect(action: AnyAction): any {
	const { rowData, schedulerId, successCB, errorCB } = action.payload;
	try {
		yield call(completeRejectProjectActivity, handleEmptyNullValues(rowData), schedulerId);
		successCB();
	} catch (error: any) {
		errorCB(error?.message);
	}
}

export function* uploadActivityDocumentEffect(action: AnyAction): any {
	const { formData, schedulerId, errorCB, successCB } = action.payload;
	const currentFileType = 'MEDIA';

	const transformedPayload = transformUploadData(formData?.document, currentFileType);
	const newFormData = {
		...formData,
		...transformedPayload
	};
	delete newFormData.transformedData;

	const transformedObject = {
		...formData,
		document: {
			referenceId: newFormData.document[0].referenceId,
			referenceType: newFormData.document[0].referenceType,
			category: newFormData.document[0].category,
			subCategory: newFormData.document[0].subCategory,
			attachments: newFormData.document[0].files.map(file => ({
				file: file.fileName
			}))
		}
	};
	try {
		const { data } = yield call(uploadActivityDocuments, transformedObject, schedulerId);
		const { attachments, category, subCategory, documentId } = data;
		const { arrPromises, uploadedDocuments } = uploadDocumentArr({ documents: [formData?.document] }, [
			{ category, subCategory, attachments, documentId }
		]);
		yield call(async () => await Promise.all(arrPromises));

		if (uploadedDocuments.length > 0) {
			yield call(activateDocument, { documentIds: uploadedDocuments });
		}
		successCB();
	} catch (error: any) {
		errorCB(error?.message);
	}
}

export function* uploadMoreActivityDocumentEffect(action: AnyAction): any {
	const { approvalId, formData, errorCB, successCB } = action.payload;
	const currentFileType = 'MEDIA';

	const transformedPayload = transformUploadData(formData?.document, currentFileType);
	const newFormData = {
		...formData,
		...transformedPayload
	};
	delete newFormData.transformedData;

	const transformedObject = {
		attachments: newFormData.document[0].files.map(file => ({
			file: file.fileName
		}))
	};

	try {
		const { data } = yield call(uploadMoreActivityDocs, approvalId, transformedObject);
		const { attachments, category, subCategory, documentId } = data;
		const { arrPromises, uploadedDocuments } = uploadDocumentArr({ documents: [formData?.document] }, [
			{ category, subCategory, attachments, documentId }
		]);
		yield call(async () => await Promise.all(arrPromises));

		if (uploadedDocuments.length > 0) {
			yield call(activateDocument, { documentIds: uploadedDocuments });
		}
		successCB();
	} catch (error: any) {
		errorCB(error?.message);
	}
}

export function* fetchActivityDocumentsEffect(action: AnyAction): any {
	const { documentIds, errorCB } = action.payload;

	try {
		const { data }: any = yield call(fetchActivityDocuments, { documentIds: documentIds });
		yield put({
			type: ProjectActionType.STORE_ACTIVITY_DOCUMENTS,
			payload: data
		});
	} catch (error: any) {
		yield put({
			type: ProjectActionType.ERROR_FETCH_ACTIVITY_DOCUMENTS,
			payload: {
				error
			}
		});
		errorCB(error?.message);
	}
}

export function* fetchProjectCurrentActivityEffect(action: AnyAction): any {
	const { projectID, errorCB } = action.payload;
	try {
		const { data }: any = yield call(getProjectCurrentActivity, projectID);
		yield put({
			type: ProjectActionType.STORE_CURRENT_ACTIVITY,
			payload: data
		});
	} catch (error: any) {
		errorCB(error?.message);
	}
}

export function* fetchAllProjectsEffect(action: AnyAction): any {
	const { projectsFilters, errorCB } = action.payload;
	const transformedFilters = handleEmptyNullValues(projectsFilters);
	try {
		const { data }: any = yield call(getAllProjects, handleEmptyNullValues(transformedFilters));
		const projectsList = data.projects;
		yield put({
			type: ProjectActionType.STORE_ALL_PROJECTS,
			payload: { projectsList, totalRecords: data.totalRecords }
		});
	} catch (error: any) {
		yield put({
			type: ProjectActionType.ERROR_FETCH_ALL_PROJECTS,
			payload: {
				error
			}
		});
		errorCB(error?.message);
	}
}

export function* fetchLedgerDataEffect(action: AnyAction): any {
	yield* requestProjectLedger(action.payload, getLedgerData, {
		store: ProjectActionType.STORE_LEDGER_DATA,
		error: ProjectActionType.ERROR_LEDGER_DATA
	});
}

export function* fetchProductCategoriesEffect(action: AnyAction): any {
	const { successCB, selectedAreas, budget, errorCB, saveToProject, projectId } = action.payload;
	try {
		const requestPayload: any = {
			areas: Array.isArray(selectedAreas) ? selectedAreas : selectedAreas?.areas,
			budget
		};

		if (saveToProject !== undefined && saveToProject !== null) {
			requestPayload.saveToProject = saveToProject;
		} else if (projectId !== undefined && projectId !== null) {
			requestPayload.projectId = projectId;
		}

		const { data }: any = yield call(fetchProductCategories, requestPayload);

		yield put({
			type: ProjectActionType.STORE_PROJECT_PRODUCT_CATEGORIES,
			payload: data
		});
		successCB();
	} catch (error: any) {
		yield put({
			type: ProjectActionType.ERROR_PROJECT_PRODUCT_CATEGORIES,
			payload: { error }
		});
		if (errorCB) {
			errorCB(error?.message);
		}
	}
}

export function* fetchTwoDJsonEffect(action: AnyAction): any {
	const { predictionId, errorCB, currentFloor } = action.payload;
	try {
		const { data }: any = yield call(fetch2DJson, predictionId, currentFloor);

		yield put({
			type: ProjectActionType.STORE_2D_JSON,
			payload: data
		});
	} catch (error: any) {
		yield put({
			type: ProjectActionType.ERROR_FETCH_2D_JSON,
			payload: { error }
		});

		errorCB(error?.message);
	}
}

export function* fetchThreeDJsonEffect(action: AnyAction): any {
	const { predictionId, errorCB, currentFloor, projectId } = action.payload;
	try {
		const { data }: any = yield call(fetch3DJson, predictionId, currentFloor);

		yield put({
			type: ProjectActionType.STORE_3D_JSON,
			payload: {
				data,
				projectId,
				currentFloor
			}
		});
	} catch (error: any) {
		yield put({
			type: ProjectActionType.ERROR_FETCH_3D_JSON,
			payload: { error }
		});
		errorCB(error?.message);
	}
}

export function* fetchLeadProjectsEffect(action: AnyAction): any {
	const { leadId, errorCB } = action.payload;

	try {
		const { data }: any = yield call(fetchLeadProjects, leadId);

		yield put({
			type: ProjectActionType.STORE_LEAD_PROJECTS,
			payload: data
		});
	} catch (error: any) {
		errorCB(error?.message);
	}
}

export function* fetchAreaListDropdownEffect(action: AnyAction): any {
	const { errorCB, quotationId } = action.payload;

	try {
		const { data }: any = yield call(fetchAreaListDropdown, quotationId);

		yield put({ type: ProjectActionType.STORE_QUOTATION_AREA_LIST_DROPDOWN, payload: data });
	} catch (error: any) {
		errorCB(error?.message);
	}
}

export function* fetchPaymentSlipsEffect(action: AnyAction): any {
	const { errorCB, paymentApprovalIds, finalCB } = action.payload;
	try {
		const { data }: any = yield call(fetchPaymentSlips, paymentApprovalIds);
		yield put({ type: ProjectActionType.STORE_PAYMENT_SLIPS, payload: data });
	} catch (error: any) {
		errorCB(error?.message);
	} finally {
		finalCB();
	}
}

const paymentsFiltersObject = (obj: any) => {
	const transformData = {
		filters: {
			...obj.filters
			// projectIds: !!obj.filters.projectName?.length ? [obj?.filters?.projectName[0]?.id] : ''
		}
	};

	return {
		...obj,
		filters: transformData.filters
	};
};

export function* fetchAllCommercialPayments(action: AnyAction): any {
	const { paymentsFilter, errorCB } = action.payload;
	const transformedData = paymentsFiltersObject(paymentsFilter);
	const transformedFilters = handleEmptyNullValues(transformedData);

	try {
		const { data }: any = yield call(getAllPayments, transformedFilters);
		yield put({
			type: ProjectActionType.STORE_ALL_PAYMENTS,
			payload: data
		});
	} catch (error: any) {
		yield put({
			type: ProjectActionType.ERROR_ALL_PAYMENTS,
			payload: {
				error
			}
		});
		errorCB(error?.message);
	}
}

export function* fetchCustomerSlipsEffect(action: AnyAction): any {
	const { errorCB, docId } = action.payload;
	try {
		const { data }: any = yield call(fetchCustomerSlip, { documentIds: [docId] });

		yield put({ type: ProjectActionType.STORE_CUSTOMER_SLIPS, payload: { data } });
	} catch (error: any) {
		errorCB(error?.message);
	}
}

export function* fetchCutlistDataEffect(action: AnyAction): any {
	const { quotationId, successCB, errorCB } = action.payload;
	try {
		const { data }: any = yield call(getCutlistData, quotationId);

		yield put({
			type: ProjectActionType.STORE_CUTLIST_DATA,
			payload: data
		});
		if (successCB) successCB(data);
	} catch (error: any) {
		if (errorCB) errorCB(error?.message);
		yield put({
			type: ProjectActionType.ERROR_CUTLIST_DATA,
			payload: error
		});
	}
}

export function* fetchOptimiserDataEffect(action: AnyAction): any {
	const { cutlistData, successCB, errorCB, projectId } = action.payload;

	try {
		const { data }: any = yield call(getOptimiserData, cutlistData?.cutlist || cutlistData, projectId);

		yield put({
			type: ProjectActionType.STORE_OPTIMISER_DATA,
			payload: { data }
		});
		if (successCB) successCB();
	} catch (error: any) {
		if (errorCB) errorCB(error?.message);
		yield put({
			type: ProjectActionType.ERROR_OPTIMISER_DATA,
			payload: error
		});
	}
}

export function* getBuilderStatesEffect(action: AnyAction): any {
	const { successCB, errorCB, quotationId } = action.payload;
	try {
		const { data }: any = yield call(getBuilderStates, quotationId);
		yield put({
			type: ProjectActionType.STORE_BUILDER_STATES,
			payload: data
		});
		if (successCB) successCB(data);
	} catch (error: any) {
		errorCB(error?.message);
	}
}

export function* fetchPromoCodeHistoryEffect(action: AnyAction): ProjectSagaEffect {
	const { successCB, errorCB } = action.payload || {};
	try {
		const { data }: any = yield call(getPromoCodeHistory);
		yield put({
			type: ProjectActionType.STORE_PROMO_CODE_HISTORY,
			payload: data.history
		});
		if (successCB) successCB(data);
	} catch (error: any) {
		yield put({
			type: ProjectActionType.ERROR_PROMO_CODE_HISTORY,
			payload: { error }
		});
		if (errorCB) errorCB(error?.message);
	}
}

export function* calculatePromoCodeAmountEffect(action: AnyAction): ProjectSagaEffect {
	const {
		currentQuotation,
		successCB,
		errorCB,
		leadId,
		projectId,
		predictionId,
		promoCodeList,
		calculationId,
		selectedAreas
	} = action.payload;
	const transformedRequest = transformQuotationRequest(currentQuotation);
	const selectedAreasPayload = transformSelectedAreasForQuotationRequest(selectedAreas);
	try {
		const { data: response }: any = yield call(calculatePromoCodeAmt, buildQuotationPromoRequest({
			...transformedRequest,
			projectId,
			leadId,
			predictionId,
			promoCodeList,
			calculationId,
			...(selectedAreasPayload ? { selectedAreas: selectedAreasPayload } : {})
		}, true));
		const rows = Array.isArray(response)
			? response
			: Array.isArray(response?.data)
				? response.data
				: Array.isArray(response?.amounts)
					? response.amounts
					: [];
		const normalized = rows.map((row: any) => ({
			...row,
			promoCode: row?.promoCode ?? row?.promo_code,
			discountAmt: row?.discountAmt ?? row?.discount_amt
		}));
		yield put({
			type: ProjectActionType.STORE_PROMO_CODE_AMOUNT,
			payload: normalized
		});
		if (successCB) successCB(normalized);
	} catch (error: any) {
		yield put({
			type: ProjectActionType.ERROR_PROMO_CODE_AMOUNT,
			payload: { error }
		});
		if (errorCB) errorCB(error?.message);
	}
}

export function* fetchOptimizedCutlistDataEffect(action: AnyAction): any {
	const { projectId, successCB, errorCB } = action.payload;
	try {
		const { data }: any = yield call(getOptimizedCutlistData, projectId);

		yield put({
			type: ProjectActionType.STORE_OPTIMISER_DATA,
			payload: { data }
		});
		if (successCB) successCB(data);
	} catch (error: any) {
		if (errorCB) errorCB(error?.message);
		yield put({
			type: ProjectActionType.ERROR_OPTIMISER_DATA,
			payload: error
		});
	}
}

export function* markCutlistViewedEffect(action: AnyAction): any {
	const { leadId, projectId, successCB, errorCB } = action.payload || {};
	try {
		yield call(markCutlistViewed, leadId, projectId);
		yield put({
			type: ProjectActionType.UPDATE_PROJECT_VIEW_FLAGS,
			payload: { cutlistViewed: true }
		});
		if (successCB) successCB();
	} catch (error: any) {
		if (errorCB) errorCB(error?.message);
	}
}

export function* markBomViewedEffect(action: AnyAction): any {
	const { leadId, projectId, successCB, errorCB } = action.payload || {};
	try {
		yield call(markBomViewed, leadId, projectId);
		yield put({
			type: ProjectActionType.UPDATE_PROJECT_VIEW_FLAGS,
			payload: { bomViewed: true }
		});
		if (successCB) successCB();
	} catch (error: any) {
		if (errorCB) errorCB(error?.message);
	}
}

export function* addProjectDetailsSaga(): ProjectSagaFormEffect {
	yield takeLatest(ProjectActionType.ADD_PROJECT_DETAILS, addProjectDetailsEffect);
}

export function* getPreDesignQuotationSaga(): ProjectSagaFormEffect {
	yield takeLatest(ProjectActionType.GET_PRE_DESIGN_QUOTATION, getPreDesignQuotationEffect);
}

export function* getDefaultQuotationsSaga(): ProjectSagaFormEffect {
	yield takeLatest(ProjectActionType.GET_DEFAULT_QUOTATION, getDefaultQuotationsEffect);
}

export function* fetchPreDesignSavedStateSaga(): ProjectSagaFormEffect {
	yield takeLatest(ProjectActionType.FETCH_PRE_DESIGN_SAVED_STATE, fetchPreDesignSavedStateEffect);
}

export function* updateFloorPlanSaga(): ProjectSagaFormEffect {
	yield takeLatest(ProjectActionType.UPLOAD_FLOOR_PLAN, updateFloorPlanEffect);
}

export function* sendCustomerOTPSaga(): ProjectSagaFormEffect {
	yield takeLatest(ProjectActionType.SEND_CUSTOMER_OTP, sendCustomerOTPEffect);
}

export function* verifyCustomerOTPSaga(): ProjectSagaFormEffect {
	yield takeLatest(ProjectActionType.VERIFY_CUSTOMER_OTP, verifyCustomerOTPEffect);
}

export function* resendCustomerOTPSaga(): ProjectSagaFormEffect {
	yield takeLatest(ProjectActionType.RESEND_CUSTOMER_OTP, resendCustomerOTPEffect);
}

export function* getPredictionIdSaga(): ProjectSagaFormEffect {
	yield takeLatest(ProjectActionType.GET_PREDICTION_ID, getPredictionIdEffect);
}

export function* getPredictionSaga(): ProjectSagaFormEffect {
	yield takeLatest(ProjectActionType.GET_PREDICTION, getPredictionEffect);
}

export function* createQuotationSaga(): ProjectSagaFormEffect {
	yield takeLatest(ProjectActionType.CREATE_QUOTATION, createQuotationEffect);
}

export function* fetchProjectDetailsSaga(): ProjectSagaFormEffect {
	yield takeLatest(ProjectActionType.FETCH_PROJECT_DETAILS, fetchProjectDetailsEffect);
}

export function* fetchProjectActivitiesSaga(): ProjectSagaFormEffect {
	yield takeLatest(ProjectActionType.FETCH_PROJECT_ACTIVITIES, fetchProjectActivitiesEffect);
}

export function* completeRejectProjectActivitySaga(): ProjectSagaFormEffect {
	yield takeLatest(ProjectActionType.COMPLETE_PROJECT_ACTIVITY, completeRejectProjectActivityEffect);
}

export function* uploadActivityDocumentSaga(): ProjectSagaFormEffect {
	yield takeLatest(ProjectActionType.UPLOAD_ACTIVITY_DOCUMENT, uploadActivityDocumentEffect);
}

export function* uploadMoreActivityDocumentSaga(): ProjectSagaFormEffect {
	yield takeLatest(ProjectActionType.UPLOAD_MORE_ACTIVITY_DOCUMENT, uploadMoreActivityDocumentEffect);
}

export function* fetchActivityDocumentsSaga(): ProjectSagaFormEffect {
	yield takeLatest(ProjectActionType.FETCH_ACTIVITY_DOCUMENTS, fetchActivityDocumentsEffect);
}

export function* fetchProjectCurrentActivitySaga(): ProjectSagaFormEffect {
	yield takeLatest(ProjectActionType.FETCH_CURRENT_ACTIVITY, fetchProjectCurrentActivityEffect);
}

export function* uploadProjectDetailsSaga(): ProjectSagaFormEffect {
	yield takeLatest(ProjectActionType.UPLOAD_PROJECT_DETAILS, uploadProjectDetailsEffect);
}

export function* fetchAllProjectsSaga(): ProjectSagaFormEffect {
	yield takeLatest(ProjectActionType.FETCH_ALL_PROJECTS, fetchAllProjectsEffect);
}

export function* fetchProductCategoriesSaga(): ProjectSagaFormEffect {
	yield takeLatest(ProjectActionType.FETCH_PROJECT_PRODUCT_CATEGORIES, fetchProductCategoriesEffect);
}

export function* fetchTwoDJsonSaga(): ProjectSagaFormEffect {
	yield takeLatest(ProjectActionType.FETCH_2D_JSON, fetchTwoDJsonEffect);
}

export function* fetchThreeDJsonSaga(): ProjectSagaFormEffect {
	yield takeLatest(ProjectActionType.FETCH_3D_JSON, fetchThreeDJsonEffect);
}

export function* fetchLeadProjectsSaga(): ProjectSagaFormEffect {
	yield takeLatest(ProjectActionType.FETCH_LEAD_PROJECTS, fetchLeadProjectsEffect);
}

export function* fetchAreaListDropdownSaga(): ProjectSagaFormEffect {
	yield takeLatest(ProjectActionType.FETCH_QUOTATION_AREA_LIST_DROPDOWN, fetchAreaListDropdownEffect);
}

export function* fetchPaymentSlipsSaga(): ProjectSagaFormEffect {
	yield takeLatest(ProjectActionType.FETCH_PAYMENT_SLIPS, fetchPaymentSlipsEffect);
}

// Warranty code details (scopes) saga
export function* fetchWarrantyDetailsEffect(action: AnyAction): ProjectSagaEffect {
	const { warrantyCodes, successCB, errorCB, finalCB } = action.payload || {};
	try {
		const { data }: any = yield call(getWarrantyCodeDetails, { warrantyCodes });
		yield put({ type: ProjectActionType.STORE_WARRANTY_DETAILS, payload: data });
		if (successCB) successCB(data);
	} catch (error: any) {
		yield put({ type: ProjectActionType.ERROR_WARRANTY_DETAILS, payload: { error } });
		if (errorCB) errorCB(error?.message);
	} finally {
		if (finalCB) finalCB();
	}
}

export function* fetchLedgerDataSaga(): ProjectSagaFormEffect {
	yield takeLatest(ProjectActionType.FETCH_LEDGER_DATA, fetchLedgerDataEffect);
}

export function* fetchAllPaymentsSaga(): ProjectSagaFormEffect {
	yield takeLatest(ProjectActionType.FETCH_ALL_PAYMENTS, fetchAllCommercialPayments);
}

export function* fetchCustomerSlipSaga(): ProjectSagaFormEffect {
	yield takeLatest(ProjectActionType.FETCH_CUSTOMER_SLIPS, fetchCustomerSlipsEffect);
}
// Warranty Claim Saga Effects
export function* checkWarrantyValidityEffect(action: AnyAction): ProjectSagaEffect {
	const { data, successCB, errorCB, finalCB } = action.payload;
	try {
		const { data: response }: any = yield call(checkWarrantyValidity, data);

		yield put({
			type: ProjectActionType.STORE_WARRANTY_VALIDITY,
			payload: response
		});

		if (successCB) {
			successCB(response);
		}
	} catch (error: any) {
		yield put({
			type: ProjectActionType.ERROR_CHECK_WARRANTY_VALIDITY,
			payload: { error }
		});

		if (errorCB) {
			errorCB(error?.message);
		}
	} finally {
		if (finalCB) {
			finalCB();
		}
	}
}

export function* createWarrantyClaimEffect(action: AnyAction): ProjectSagaEffect {
	const { data, successCB, errorCB, finalCB } = action.payload;

	try {
		let docId: string | null = null;
		let paymentDocId: string | null = null;

		// Upload warranty claim documents if files exist
		if (data.files && data.files.length > 0) {
			const currentFileType = 'MEDIA';

			// Create document structure for transformUploadData helper
			const documentData = {
				referenceId: data.productId,
				referenceType: 'PRODUCT',
				category: 'WARRANTY_CLAIM',
				subCategory: 'WARRANTY_CLAIM_DOCS',
				files: data.files,
				type: currentFileType
			};

			// Transform upload data to get file names
			const transformedPayload = transformUploadData(documentData, currentFileType);
			const newFormData = {
				...transformedPayload
			};

			// Create transformed object matching API contract with camelCase
			const transformedObject = {
				referenceId: data.productId,
				referenceType: 'WARRANTY_CLAIM',
				documents: [
					{
						category: 'TICKET',
						subCategory: 'TICKET',
						attachments: newFormData.document[0].files.map((file: any) => ({
							file: file.fileName,
							coverImage: false
						}))
					}
				]
			};

			const { data: uploadResponse }: any = yield call(uploadWarrantyClaimDocuments, transformedObject);

			const uploadedDocument = uploadResponse.documentsUploadResponses;

			// Create document structure with original File objects for S3 upload
			const documentForUpload = {
				...newFormData.document[0],
				category: 'TICKET',
				subCategory: 'TICKET',
				files: data.files // Keep original File objects for S3 upload
			};

			const { arrPromises, uploadedDocuments } = uploadDocumentArr({ documents: [documentForUpload] }, [
				uploadedDocument
			]);

			yield call(async () => await Promise.all(arrPromises));

			if (uploadedDocuments.length > 0) {
				yield call(activateDocument, { documentIds: uploadedDocuments });
				docId = uploadedDocuments[0];
			}
		}

		// Upload payment receipt documents if they exist (for out of warranty)
		if (data.paymentReceiptFiles && data.paymentReceiptFiles.length > 0) {
			const currentFileType = 'MEDIA';

			const paymentDocumentData = {
				referenceId: data.productId,
				referenceType: 'PRODUCT',
				category: 'WARRANTY_CLAIM',
				subCategory: 'PAYMENT_RECEIPT',
				files: data.paymentReceiptFiles,
				type: currentFileType
			};

			const transformedPayload = transformUploadData(paymentDocumentData, currentFileType);
			const newFormData = {
				...transformedPayload
			};

			const transformedObject = {
				referenceId: data.productId,
				referenceType: 'WARRANTY_CLAIM',
				documents: [
					{
						category: 'TICKET',
						subCategory: 'TICKET',
						attachments: newFormData.document[0].files.map((file: any) => ({
							file: file.fileName,
							coverImage: false
						}))
					}
				]
			};

			const { data: uploadResponse }: any = yield call(uploadWarrantyClaimDocuments, transformedObject);

			const uploadedDocument = uploadResponse.documentsUploadResponses;

			// Create document structure with original File objects for S3 upload
			const paymentDocumentForUpload = {
				...newFormData.document[0],
				category: 'TICKET',
				subCategory: 'TICKET',
				files: data.paymentReceiptFiles // Keep original File objects for S3 upload
			};

			const { arrPromises, uploadedDocuments } = uploadDocumentArr({ documents: [paymentDocumentForUpload] }, [
				uploadedDocument
			]);

			yield call(async () => await Promise.all(arrPromises));

			if (uploadedDocuments.length > 0) {
				yield call(activateDocument, { documentIds: uploadedDocuments });
				paymentDocId = uploadedDocuments[0];
			}
		}

		const claimData = {
			...data,
			docId: docId,
			paymentDocId: paymentDocId,
			floor: data.floorNumber,
			serviceCharges: data.serviceCharge
		};

		delete claimData.floorNumber;
		delete claimData.serviceCharge;
		delete claimData.files;
		delete claimData.paymentReceiptFiles;

		const { data: response }: any = yield call(createWarrantyClaim, claimData);

		yield put({
			type: ProjectActionType.STORE_WARRANTY_CLAIM,
			payload: response
		});

		if (successCB) {
			successCB(response);
		}
	} catch (error: any) {
		yield put({
			type: ProjectActionType.ERROR_CREATE_WARRANTY_CLAIM,
			payload: { error }
		});

		if (errorCB) {
			errorCB(error?.message);
		}
	} finally {
		if (finalCB) {
			finalCB();
		}
	}
}

export function* getWarrantyClaimEffect(action: AnyAction): ProjectSagaEffect {
	const { warrantyClaimId, successCB, errorCB, finalCB } = action.payload;

	try {
		const { data }: any = yield call(getWarrantyClaim, warrantyClaimId);

		yield put({
			type: ProjectActionType.STORE_WARRANTY_CLAIM_DETAILS,
			payload: data
		});

		if (successCB) {
			successCB(data);
		}
	} catch (error: any) {
		yield put({
			type: ProjectActionType.ERROR_GET_WARRANTY_CLAIM,
			payload: { error }
		});

		if (errorCB) {
			errorCB(error?.message);
		}
	} finally {
		if (finalCB) {
			finalCB();
		}
	}
}

export function* getAllWarrantyClaimsEffect(action: AnyAction): ProjectSagaEffect {
	const { data, successCB, errorCB, finalCB } = action.payload;

	try {
		const { data: response }: any = yield call(getAllWarrantyClaims, data);

		yield put({
			type: ProjectActionType.STORE_ALL_WARRANTY_CLAIMS,
			payload: response
		});

		if (successCB) {
			successCB(response);
		}
	} catch (error: any) {
		yield put({
			type: ProjectActionType.ERROR_GET_ALL_WARRANTY_CLAIMS,
			payload: { error }
		});

		if (errorCB) {
			errorCB(error?.message);
		}
	} finally {
		if (finalCB) {
			finalCB();
		}
	}
}

export function* getWarrantyClaimsByLeadEffect(action: AnyAction): ProjectSagaEffect {
	const { leadId, successCB, errorCB, finalCB } = action.payload;

	try {
		const { data: response }: any = yield call(getWarrantyClaimsByLeadId, leadId);

		yield put({
			type: ProjectActionType.STORE_WARRANTY_CLAIMS_BY_LEAD,
			payload: response
		});

		if (successCB) {
			successCB(response);
		}
	} catch (error: any) {
		yield put({
			type: ProjectActionType.ERROR_GET_WARRANTY_CLAIMS_BY_LEAD,
			payload: { error }
		});

		if (errorCB) {
			errorCB(error?.message);
		}
	} finally {
		if (finalCB) {
			finalCB();
		}
	}
}

export function* fetchAllWarrantyEffect(action: AnyAction): ProjectSagaEffect {
	const { leadId, successCB, errorCB, finalCB } = action.payload;

	try {
		const { data: response }: any = yield call(getWarrantyClaimsByLeadId, leadId);

		yield put({
			type: ProjectActionType.STORE_ALL_WARRANTY,
			payload: response
		});

		if (successCB) {
			successCB(response);
		}
	} catch (error: any) {
		yield put({
			type: ProjectActionType.ERROR_FETCH_ALL_WARRANTY,
			payload: { error }
		});

		if (errorCB) {
			errorCB(error?.message);
		}
	} finally {
		if (finalCB) {
			finalCB();
		}
	}
}

// Warranty Claim Saga Watchers
export function* checkWarrantyValiditySaga(): ProjectSagaFormEffect {
	yield takeLatest(ProjectActionType.CHECK_WARRANTY_VALIDITY, checkWarrantyValidityEffect);
}

export function* createWarrantyClaimSaga(): ProjectSagaFormEffect {
	yield takeLatest(ProjectActionType.CREATE_WARRANTY_CLAIM, createWarrantyClaimEffect);
}

export function* getWarrantyClaimSaga(): ProjectSagaFormEffect {
	yield takeLatest(ProjectActionType.GET_WARRANTY_CLAIM, getWarrantyClaimEffect);
}

export function* getAllWarrantyClaimsSaga(): ProjectSagaFormEffect {
	yield takeLatest(ProjectActionType.GET_ALL_WARRANTY_CLAIMS, getAllWarrantyClaimsEffect);
}

export function* getWarrantyClaimsByLeadSaga(): ProjectSagaFormEffect {
	yield takeLatest(ProjectActionType.GET_WARRANTY_CLAIMS_BY_LEAD, getWarrantyClaimsByLeadEffect);
}

export function* fetchAllWarrantySaga(): ProjectSagaFormEffect {
	yield takeLatest(ProjectActionType.FETCH_ALL_WARRANTY, fetchAllWarrantyEffect);
}

export function* fetchWarrantyDetailsSaga(): ProjectSagaFormEffect {
	yield takeLatest(ProjectActionType.FETCH_WARRANTY_DETAILS, fetchWarrantyDetailsEffect);
}

export function* updateWarrantyPaymentEffect(action: AnyAction): ProjectSagaEffect {
	const { warrantyClaimId, files, successCB, errorCB, finalCB } = action.payload;

	try {
		let docId: string | null = null;

		if (files && files.length > 0) {
			const currentFileType = 'MEDIA';

			const documentData = {
				referenceId: warrantyClaimId.toString(),
				referenceType: 'WARRANTY_CLAIM',
				category: 'WARRANTY_CLAIM',
				subCategory: 'PAYMENT_RECEIPT',
				files: files,
				type: currentFileType
			};

			const transformedPayload = transformUploadData(documentData, currentFileType);
			const newFormData = {
				...transformedPayload
			};

			const transformedObject = {
				referenceId: warrantyClaimId.toString(),
				referenceType: 'WARRANTY_CLAIM',
				documents: [
					{
						category: 'TICKET',
						subCategory: 'TICKET',
						attachments: newFormData.document[0].files.map((file: any) => ({
							file: file.fileName,
							coverImage: false
						}))
					}
				]
			};

			const { data: uploadResponse }: any = yield call(uploadWarrantyClaimDocuments, transformedObject);

			const uploadedDocument = uploadResponse.documentsUploadResponses;

			const { arrPromises, uploadedDocuments } = uploadDocumentArr(
				{ documents: [transformedObject?.documents[0]] },
				[uploadedDocument]
			);

			yield call(async () => await Promise.all(arrPromises));

			if (uploadedDocuments.length > 0) {
				yield call(activateDocument, { documentIds: uploadedDocuments });
				docId = uploadedDocuments[0];
			}
		}

		const paymentApprovalId = Date.now();

		yield put({
			type: ProjectActionType.STORE_WARRANTY_PAYMENT_UPDATE,
			payload: {
				warrantyClaimId,
				paymentApprovalId,
				docId
			}
		});

		if (successCB) {
			successCB({ paymentApprovalId, docId });
		}
	} catch (error: any) {
		yield put({
			type: ProjectActionType.ERROR_UPDATE_WARRANTY_PAYMENT,
			payload: { error }
		});

		if (errorCB) {
			errorCB(error?.message);
		}
	} finally {
		if (finalCB) {
			finalCB();
		}
	}
}

export function* updateWarrantyPaymentSaga(): ProjectSagaFormEffect {
	yield takeLatest(ProjectActionType.UPDATE_WARRANTY_PAYMENT, updateWarrantyPaymentEffect);
}

export function* fetchCutlistDataSaga(): ProjectSagaFormEffect {
	yield takeLatest(ProjectActionType.FETCH_CUTLIST_DATA, fetchCutlistDataEffect);
}

export function* fetchOptimiserDataSaga(): ProjectSagaFormEffect {
	yield takeLatest(ProjectActionType.FETCH_OPTIMISER_DATA, fetchOptimiserDataEffect);
}

export function* fetchOptimizedCutlistDataSaga(): ProjectSagaFormEffect {
	yield takeLatest(ProjectActionType.FETCH_OPTIMIZED_CUTLIST_DATA, fetchOptimizedCutlistDataEffect);
}

export function* markCutlistViewedSaga(): ProjectSagaFormEffect {
	yield takeLatest(ProjectActionType.MARK_CUTLIST_VIEWED, markCutlistViewedEffect);
}

export function* markBomViewedSaga(): ProjectSagaFormEffect {
	yield takeLatest(ProjectActionType.MARK_BOM_VIEWED, markBomViewedEffect);
}

export function* fetchAssignedProjectsEffect(action: AnyAction): ProjectSagaEffect {
	const { successCB, errorCB } = action.payload || {};
	try {
		const { data }: any = yield call(getAssignedProjects);
		yield put({
			type: ProjectActionType.STORE_ASSIGNED_PROJECTS,
			payload: data
		});
		if (successCB) successCB(data);
	} catch (error: any) {
		yield put({
			type: ProjectActionType.ERROR_ASSIGNED_PROJECTS,
			payload: { error }
		});
		if (errorCB) errorCB(error?.message);
	}
}

export function* fetchAssignedProjectsSaga(): ProjectSagaFormEffect {
	yield takeLatest(ProjectActionType.FETCH_ASSIGNED_PROJECTS, fetchAssignedProjectsEffect);
}

export function* fetchBuilderStatesSaga(): ProjectSagaFormEffect {
	yield takeLatest(ProjectActionType.FETCH_BUILDER_STATES, getBuilderStatesEffect);
}

export function* fetchPromoCodeHistorySaga(): ProjectSagaFormEffect {
	yield takeLatest(ProjectActionType.FETCH_PROMO_CODE_HISTORY, fetchPromoCodeHistoryEffect);
}

export function* calculatePromoCodeAmountSaga(): ProjectSagaFormEffect {
	yield takeLatest(ProjectActionType.CALCULATE_PROMO_CODE_AMOUNT, calculatePromoCodeAmountEffect);
}
